<template>
  <registry-page
    title="Номенклатура"
    :description="pageDescription"
  >
    <app-data-table
      data-testid="warehouse-nomenclature-table"
      title="Товары и услуги"
      storage-key="nomenclature-directory-table"
      :count="visibleRows.length"
      :rows="visibleRows"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-message="errorMessage"
      @update:pagination="pagination = $event"
      @row-click="openEditFromRow"
      @reset="resetFilters"
      @retry="loadDirectory"
    >
      <template #filters>
        <filter-bar
          :model-value="search"
          search-label="Поиск по коду, названию, артикулу или штрихкоду"
          search-test-id="nomenclature-directory-search"
          :has-filters="hasFilters"
          :applied-filters="appliedFilters"
          @search="applySearch"
          @reset="resetFilters"
          @remove="removeFilter"
        >
          <template #quick>
            <q-select
              dense
              outlined
              emit-value
              map-options
              label="Статус"
              class="nomenclature-status-filter"
              :model-value="statusFilter"
              :options="statusOptions"
              data-testid="nomenclature-directory-status"
              @update:model-value="applyStatus"
            />
          </template>
          <template #advanced>
            <q-select
              outlined
              emit-value
              map-options
              label="Статус"
              :model-value="statusFilter"
              :options="statusOptions"
              @update:model-value="applyStatus"
            />
          </template>
        </filter-bar>
      </template>

      <template v-if="canEdit" #actions>
        <q-btn
          unelevated
          color="primary"
          icon="add"
          no-caps
          label="Добавить"
          data-testid="warehouse-nomenclature-add"
          @click="openCreateDialog"
        />
      </template>

      <template #body-cell-common_name="props">
        <q-td :props="props">
          <div class="nomenclature-name">{{ props.row.common_name || props.row.name || 'Без названия' }}</div>
          <div v-if="props.row.article" class="text-caption text-grey-7">Артикул {{ props.row.article }}</div>
        </q-td>
      </template>

      <template #body-cell-item_type="props">
        <q-td :props="props">
          <status-badge
            :tone="itemType(props.row) === 'service' ? 'info' : 'neutral'"
            :icon="itemType(props.row) === 'service' ? 'design_services' : 'inventory_2'"
            :label="itemType(props.row) === 'service' ? 'Услуга' : 'Товар'"
          />
        </q-td>
      </template>

      <template #body-cell-min_quantity="props">
        <q-td :props="props" class="text-right nomenclature-number">
          {{ thresholdLabel(props.row) }}
        </q-td>
      </template>

      <template #body-cell-status="props">
        <q-td :props="props">
          <status-badge
            :tone="props.row.is_active === false ? 'neutral' : 'success'"
            :icon="props.row.is_active === false ? 'archive' : 'check_circle'"
            :label="props.row.is_active === false ? 'Архив' : 'Активна'"
          />
        </q-td>
      </template>

      <template #body-cell-actions="props">
        <q-td :props="props" class="text-right">
          <q-btn
            v-if="canEdit"
            flat
            dense
            round
            color="primary"
            icon="edit"
            aria-label="Редактировать номенклатуру"
            data-testid="warehouse-nomenclature-edit"
            :disable="pending"
            @click.stop="openEditDialog(props.row)"
          />
          <q-btn
            v-if="canEdit && props.row.is_active !== false"
            flat
            dense
            round
            color="negative"
            icon="archive"
            aria-label="Архивировать номенклатуру"
            data-testid="warehouse-nomenclature-archive"
            :disable="pending"
            @click.stop="openArchiveDialog(props.row)"
          />
        </q-td>
      </template>
    </app-data-table>

    <q-dialog v-model="formDialog" :persistent="pending">
      <q-card class="directory-dialog" @keydown.esc="closeFormDialog">
        <q-card-section class="row items-center">
          <h2 class="directory-dialog__title">{{ editMode ? 'Редактировать номенклатуру' : 'Новая номенклатура' }}</h2>
          <q-space />
          <q-btn flat round dense icon="close" aria-label="Закрыть" :disable="pending" @click="closeFormDialog" />
        </q-card-section>
        <q-separator />
        <q-card-section>
          <q-banner v-if="formError" rounded class="bg-red-1 text-negative q-mb-md" data-testid="warehouse-nomenclature-form-error">
            {{ formError }}
          </q-banner>
          <form-grid>
            <q-select
              v-model="form.item_type"
              dense
              outlined
              emit-value
              map-options
              label="Тип"
              :options="itemTypeOptions"
              :disable="pending"
              data-testid="nomenclature-directory-type"
            />
            <q-input
              ref="codeInput"
              v-model.trim="form.code"
              dense
              outlined
              label="Код"
              :disable="pending || editMode"
              data-testid="warehouse-nomenclature-code"
            />
            <q-input
              v-model.trim="form.common_name"
              dense
              outlined
              label="Название"
              :disable="pending"
              data-testid="warehouse-nomenclature-name"
            />
            <q-select
              v-model="form.unit_code"
              dense
              outlined
              emit-value
              map-options
              label="Единица измерения"
              :options="unitOptions"
              :disable="pending || loadingUnits"
              data-testid="warehouse-nomenclature-unit"
              @update:model-value="syncUnitName"
            />
            <q-input v-model.trim="form.article" dense outlined label="Артикул" :disable="pending" data-testid="warehouse-nomenclature-article" />
            <q-input v-model.trim="form.barcode" dense outlined label="Штрихкод" :disable="pending" data-testid="warehouse-nomenclature-barcode" />
            <q-input
              v-model="form.min_quantity"
              dense
              outlined
              type="number"
              min="0"
              step="0.0001"
              label="Минимальный остаток"
              hint="Используется в контроле низких остатков"
              :disable="pending"
              data-testid="warehouse-nomenclature-min-quantity"
            />
            <q-input
              v-model="form.reorder_threshold"
              dense
              outlined
              type="number"
              min="0"
              step="0.0001"
              label="Порог пополнения"
              :disable="pending"
              data-testid="warehouse-nomenclature-reorder-threshold"
            />
          </form-grid>
        </q-card-section>
        <q-card-actions align="right" class="directory-dialog__actions">
          <q-btn flat no-caps label="Отмена" :disable="pending" @click="closeFormDialog" />
          <q-btn
            unelevated
            no-caps
            color="primary"
            label="Сохранить"
            data-testid="warehouse-nomenclature-save"
            :loading="pending"
            @click="saveNomenclature"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <confirm-dialog
      v-model="archiveDialog"
      title="Архивировать номенклатуру"
      :body="archiveDialogBody"
      confirm-label="Архивировать"
      destructive
      :pending="pending"
      @cancel="cancelArchive"
      @confirm="archiveNomenclature"
    />
  </registry-page>
</template>

<script>
import {
  AppDataTable,
  ConfirmDialog,
  FilterBar,
  FormGrid,
  RegistryPage,
  StatusBadge
} from '../../components/ui'

const DEFAULT_UNITS = [
  { code: 'pcs', name: 'шт', display_name: 'штука' },
  { code: 'kg', name: 'кг', display_name: 'килограмм' },
  { code: 'm', name: 'м', display_name: 'метр' },
  { code: 'l', name: 'л', display_name: 'литр' }
]

function emptyForm() {
  return {
    id: null,
    code: '',
    common_name: '',
    item_type: 'product',
    unit_code: 'pcs',
    unit_name: 'шт',
    article: '',
    barcode: '',
    min_quantity: '',
    reorder_threshold: '',
    metadata: {}
  }
}

export default {
  name: 'NomenclatureDirectory',
  components: {
    AppDataTable,
    ConfirmDialog,
    FilterBar,
    FormGrid,
    RegistryPage,
    StatusBadge
  },
  data() {
    const query = this.$route.query || {}
    return {
      rows: [],
      units: [...DEFAULT_UNITS],
      loading: false,
      loadingUnits: false,
      pending: false,
      accessDenied: false,
      errorMessage: '',
      formError: '',
      requestId: null,
      unitsRequestId: null,
      mutationRequestId: null,
      search: String(query.q || ''),
      statusFilter: String(query.status || 'active'),
      formDialog: false,
      archiveDialog: false,
      editMode: false,
      selectedRow: null,
      form: emptyForm(),
      pagination: { page: 1, rowsPerPage: 20, rowsNumber: 0, sortBy: 'common_name', descending: false },
      statusOptions: [
        { label: 'Активные', value: 'active' },
        { label: 'Архивные', value: 'archived' },
        { label: 'Все', value: 'all' }
      ],
      itemTypeOptions: [
        { label: 'Товар', value: 'product' },
        { label: 'Услуга', value: 'service' }
      ],
      columns: [
        { name: 'code', align: 'left', label: 'Код', field: 'code', sortable: true, required: true },
        { name: 'common_name', align: 'left', label: 'Название', field: 'common_name', sortable: true, required: true },
        { name: 'item_type', align: 'left', label: 'Тип', field: 'metadata' },
        { name: 'unit_name', align: 'left', label: 'Ед.', field: 'unit_name' },
        { name: 'min_quantity', align: 'right', label: 'Мин. / пополнение', field: 'min_quantity', classes: 'column-priority-secondary', headerClasses: 'column-priority-secondary' },
        { name: 'status', align: 'left', label: 'Статус', field: 'is_active' },
        { name: 'actions', align: 'right', label: 'Действия', field: 'id', required: true }
      ],
      handlers: []
    }
  },
  computed: {
    permissions() {
      return this.$store.state.permissions || {}
    },
    canView() {
      return this.permissions.open_stock === true || this.permissions.edit_stock === true || this.permissions.open_fin_documents === true || this.permissions.edit_fin_documents === true
    },
    canEdit() {
      return this.permissions.edit_stock === true || this.permissions.edit_fin_documents === true
    },
    pageDescription() {
      return this.$route.name === 'accounting_nomenclature'
        ? 'Единый справочник товаров и услуг для УПД, бухгалтерских документов и складского учета.'
        : 'Единый справочник товаров и услуг для складских движений и бухгалтерских документов.'
    },
    hasFilters() {
      return Boolean(this.search || this.statusFilter !== 'active')
    },
    appliedFilters() {
      const filters = []
      if (this.search) filters.push({ key: 'search', label: `Поиск: ${this.search}` })
      if (this.statusFilter !== 'active') {
        const option = this.statusOptions.find((item) => item.value === this.statusFilter)
        filters.push({ key: 'status', label: `Статус: ${option ? option.label : this.statusFilter}` })
      }
      return filters
    },
    visibleRows() {
      const search = this.search.trim().toLocaleLowerCase('ru')
      return this.rows.filter((row) => {
        const active = row.is_active !== false
        if (this.statusFilter === 'active' && !active) return false
        if (this.statusFilter === 'archived' && active) return false
        if (!search) return true
        return [row.code, row.common_name, row.name, row.article, row.barcode]
          .some((value) => String(value || '').toLocaleLowerCase('ru').includes(search))
      })
    },
    tableState() {
      if (!this.canView || this.accessDenied) return 'access-denied'
      if (this.errorMessage) return 'error'
      if (this.loading && this.rows.length === 0) return 'loading'
      if (this.visibleRows.length === 0) return this.hasFilters ? 'filtered-empty' : 'empty'
      return 'ready'
    },
    unitOptions() {
      return this.units.map((unit) => ({
        label: `${unit.name} — ${unit.display_name || unit.code}`,
        value: unit.code
      }))
    },
    archiveDialogBody() {
      const row = this.selectedRow || {}
      return `«${row.common_name || row.name || 'Без названия'}» останется в ранее созданных документах, но будет скрыта из активного справочника.`
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(query) {
        const previousRequestKey = `${this.search}|${this.statusFilter}`
        this.search = String(query.q || '')
        this.statusFilter = String(query.status || 'active')
        const nextRequestKey = `${this.search}|${this.statusFilter}`
        if (previousRequestKey !== nextRequestKey && this.canView) this.loadRows()
      }
    }
  },
  mounted() {
    this.bind('onListNomenclature', this.onRows)
    this.bind('onListUnits', this.onUnits)
    this.bind('onCreateNomenclature', this.onMutation)
    this.bind('onUpdateNomenclature', this.onMutation)
    this.bind('onArchiveNomenclature', this.onMutation)
    this.bind('accessDeniedErrorReceived', this.onAccessDenied)
    const ready = () => this.loadDirectory()
    this.handlers.push(['connectionReady', ready])
    if (this.connection.isAuthenticated) ready()
    else this.emitter.on('connectionReady', ready)
  },
  beforeUnmount() {
    this.handlers.forEach(([name, handler]) => this.emitter.off(name, handler))
  },
  methods: {
    bind(name, handler) {
      this.handlers.push([name, handler])
      this.emitter.on(name, handler)
    },
    pushQuery(changes) {
      const query = { ...this.$route.query, ...changes }
      Object.keys(query).forEach((key) => {
        if (query[key] === null || query[key] === undefined || query[key] === '') delete query[key]
      })
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.push({ query })
    },
    applySearch(value) {
      this.pushQuery({ q: value || null })
    },
    applyStatus(value) {
      this.pushQuery({ status: value === 'active' ? null : value })
    },
    removeFilter(key) {
      if (key === 'search') this.applySearch('')
      if (key === 'status') this.applyStatus('active')
    },
    resetFilters() {
      this.pushQuery({ q: null, status: null })
    },
    loadDirectory() {
      if (!this.canView) {
        this.loading = false
        return
      }
      this.loadRows()
      this.loadingUnits = true
      this.unitsRequestId = this.connection.makeRequestId('nomenclature-units')
      this.connection.listUnits(this.unitsRequestId)
    },
    loadRows() {
      this.loading = true
      this.accessDenied = false
      this.errorMessage = ''
      this.requestId = this.connection.makeRequestId('nomenclature-directory')
      this.connection.listNomenclature(this.requestId, {
        query: this.search,
        search: this.search,
        include_archived: true
      })
    },
    onRows(response) {
      if (response.request_id !== this.requestId) return
      this.loading = false
      if (response.type === 'error' || response.error) {
        this.rows = []
        this.accessDenied = response.error === 'access_denied'
        this.errorMessage = this.accessDenied ? '' : (response.message || 'Не удалось загрузить номенклатуру')
        return
      }
      this.rows = response.nomenclature || response.abstract_positions || []
      this.pagination = { ...this.pagination, rowsNumber: this.rows.length }
    },
    onAccessDenied(response) {
      if (response.request_id === this.mutationRequestId) {
        this.pending = false
        if (this.formDialog) this.formError = 'Недостаточно прав для изменения номенклатуры'
        else this.errorMessage = 'Недостаточно прав для архивации номенклатуры'
        return
      }
      if (response.request_id !== this.requestId) return
      this.loading = false
      this.rows = []
      this.accessDenied = true
      this.errorMessage = ''
    },
    onUnits(response) {
      if (response.request_id !== this.unitsRequestId) return
      this.loadingUnits = false
      if (response.type !== 'error' && !response.error && Array.isArray(response.units) && response.units.length) {
        this.units = response.units
      }
    },
    itemType(row) {
      const metadata = row.metadata || {}
      if (row.is_service === true || metadata.item_type === 'service' || metadata.kind === 'service') return 'service'
      return 'product'
    },
    thresholdLabel(row) {
      const minimum = row.min_quantity ?? (row.metadata && row.metadata.min_quantity)
      const reorder = row.reorder_threshold ?? (row.metadata && row.metadata.reorder_threshold)
      const minimumLabel = minimum === '' || minimum === null || minimum === undefined ? '—' : String(minimum)
      const reorderLabel = reorder === '' || reorder === null || reorder === undefined ? '—' : String(reorder)
      return `${minimumLabel} / ${reorderLabel}`
    },
    openCreateDialog() {
      this.editMode = false
      this.form = emptyForm()
      this.formError = ''
      this.formDialog = true
    },
    openEditDialog(row) {
      this.editMode = true
      this.form = {
        ...emptyForm(),
        id: row.id || row.primary,
        code: row.code || '',
        common_name: row.common_name || row.name || '',
        item_type: this.itemType(row),
        unit_code: row.unit_code || 'pcs',
        unit_name: row.unit_name || 'шт',
        article: row.article || '',
        barcode: row.barcode || '',
        min_quantity: row.min_quantity ?? (row.metadata && row.metadata.min_quantity) ?? '',
        reorder_threshold: row.reorder_threshold ?? (row.metadata && row.metadata.reorder_threshold) ?? '',
        metadata: { ...(row.metadata || {}) }
      }
      this.formError = ''
      this.formDialog = true
    },
    openEditFromRow(event, row) {
      if (this.canEdit && row) this.openEditDialog(row)
    },
    closeFormDialog() {
      if (!this.pending) this.formDialog = false
    },
    syncUnitName(code) {
      const unit = this.units.find((item) => item.code === code)
      this.form.unit_name = unit ? unit.name : ''
    },
    saveNomenclature() {
      if (this.pending) return
      if (!String(this.form.code || '').trim() || !String(this.form.common_name || '').trim() || !String(this.form.unit_code || '').trim()) {
        this.formError = 'Заполните код, название и единицу измерения'
        this.$nextTick(() => this.$refs.codeInput && this.$refs.codeInput.focus())
        return
      }
      this.pending = true
      this.formError = ''
      this.syncUnitName(this.form.unit_code)
      const payload = {
        code: this.form.code,
        common_name: this.form.common_name,
        unit_code: this.form.unit_code,
        unit_name: this.form.unit_name,
        article: this.form.article,
        min_quantity: this.form.min_quantity,
        reorder_threshold: this.form.reorder_threshold,
        barcode: this.form.barcode,
        metadata: { ...this.form.metadata, item_type: this.form.item_type }
      }
      this.mutationRequestId = this.connection.makeRequestId(this.editMode ? 'nomenclature-update' : 'nomenclature-create')
      if (this.editMode) this.connection.updateNomenclature(this.mutationRequestId, this.form.id, payload)
      else this.connection.createNomenclature(this.mutationRequestId, payload)
    },
    openArchiveDialog(row) {
      this.selectedRow = row
      this.archiveDialog = true
    },
    cancelArchive() {
      if (!this.pending) {
        this.archiveDialog = false
        this.selectedRow = null
      }
    },
    archiveNomenclature() {
      if (this.pending || !this.selectedRow) return
      this.pending = true
      this.mutationRequestId = this.connection.makeRequestId('nomenclature-archive')
      this.connection.archiveNomenclature(this.mutationRequestId, this.selectedRow.id || this.selectedRow.primary)
    },
    onMutation(response) {
      if (response.request_id !== this.mutationRequestId) return
      this.pending = false
      if (response.type === 'error' || response.error) {
        const message = response.message || 'Не удалось выполнить операцию с номенклатурой'
        if (this.formDialog) this.formError = message
        else this.errorMessage = message
        return
      }
      const archived = response.action === 'archive_nomenclature'
      this.formDialog = false
      this.archiveDialog = false
      this.selectedRow = null
      this.$q.notify({ color: 'positive', message: archived ? 'Номенклатура архивирована' : 'Номенклатура сохранена' })
      this.loadRows()
    }
  }
}
</script>

<style scoped>
.nomenclature-status-filter { min-width: 11rem; }
.nomenclature-name { max-width: 34rem; overflow-wrap: anywhere; font-weight: var(--sid-font-weight-medium); }
.nomenclature-number { font-variant-numeric: tabular-nums; }
.directory-dialog { width: min(54rem, calc(100vw - 2 * var(--sid-space-4))); max-width: 100%; border-radius: var(--sid-radius-large); }
.directory-dialog__title { margin: 0; font-size: var(--sid-font-size-section); line-height: var(--sid-line-height-section); }
.directory-dialog__actions { gap: var(--sid-space-2); padding: 0 var(--sid-space-4) var(--sid-space-4); }

@media (max-width: 599px) {
  .directory-dialog__actions { align-items: stretch; flex-direction: column-reverse; }
  .directory-dialog__actions :deep(.q-btn) { width: 100%; min-height: var(--sid-touch-target); }
}
</style>
